/**
 * Preferences slice — the single source of truth for theme, tenant, locale,
 * the current user's role, and the (admin-only, off-by-default) data-lineage
 * overlay. Initial state is hydrated from the preference store; changes are
 * mirrored back to it via the persist middleware.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Theme, Tenant } from "@oxbo/tokens";
import type { Locale } from "@i18n/locales";
import { loadPreferences } from "@lib/preferences";

/** User role gate. `data_admin` is the only role that may see data lineage. */
export type UserRole = "field" | "data_admin";

export interface PrefsState {
  theme: Theme;
  tenant: Tenant;
  locale: Locale;
  role: UserRole;
  /** Left-nav collapsed (icon-only rail) vs expanded (icon + label). Persisted. */
  navCollapsed: boolean;
  /**
   * Chosen unit-system display profile key, or `null` for Auto (follow the org
   * default / each record's own stored profile). Persisted. Changing it only
   * re-labels + re-converts what is shown; stored values never move.
   */
  displayProfile: string | null;
  /** Optional display-currency override (ISO code), or `null` for native. Persisted. */
  displayCurrency: string | null;
  /** Data-lineage overlay. Only meaningful for data_admin; never persisted. */
  lineageOverlay: boolean;
}

const persisted = loadPreferences();

const initialState: PrefsState = {
  theme: persisted.theme,
  tenant: persisted.tenant,
  locale: persisted.locale,
  navCollapsed: persisted.navCollapsed,
  displayProfile: persisted.displayProfile,
  displayCurrency: persisted.displayCurrency,
  // Field-user-first: default role is the field user. A real deployment sets
  // this from the authenticated session / Oxbo-M user profile.
  role: "field",
  lineageOverlay: false,
};

const prefsSlice = createSlice({
  name: "prefs",
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<Theme>) {
      state.theme = action.payload;
    },
    setTenant(state, action: PayloadAction<Tenant>) {
      state.tenant = action.payload;
    },
    setLocale(state, action: PayloadAction<Locale>) {
      state.locale = action.payload;
    },
    setNavCollapsed(state, action: PayloadAction<boolean>) {
      state.navCollapsed = action.payload;
    },
    /** Choose a display profile; `null` = Auto (follow org/record default). */
    setDisplayProfile(state, action: PayloadAction<string | null>) {
      state.displayProfile = action.payload;
    },
    /** Choose a display currency; `null` = show each value's native currency. */
    setDisplayCurrency(state, action: PayloadAction<string | null>) {
      state.displayCurrency = action.payload;
    },
    toggleNavCollapsed(state) {
      state.navCollapsed = !state.navCollapsed;
    },
    setRole(state, action: PayloadAction<UserRole>) {
      state.role = action.payload;
      // Losing admin also drops the overlay so it can never linger visible.
      if (action.payload !== "data_admin") state.lineageOverlay = false;
    },
    toggleLineageOverlay(state) {
      // Guarded: only data_admin can turn it on.
      if (state.role !== "data_admin") {
        state.lineageOverlay = false;
        return;
      }
      state.lineageOverlay = !state.lineageOverlay;
    },
  },
});

export const {
  setTheme,
  setTenant,
  setLocale,
  setNavCollapsed,
  setDisplayProfile,
  setDisplayCurrency,
  toggleNavCollapsed,
  setRole,
  toggleLineageOverlay,
} = prefsSlice.actions;
export const prefsReducer = prefsSlice.reducer;
